import styled from "@emotion/styled";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { i18n } from "src/common";
import { CardWithTitle, checkIfOlderThan18, InputBirthDate, InputText } from "src/components";
import { FormRef } from "./form";
import dayjs from "dayjs";

export type InsuredPersonFormItemType = 'firstName' | 'lastName' | 'birthDate';
export const allInsuredPersonDataFormItems: Array<InsuredPersonFormItemType> = ['firstName', 'lastName', 'birthDate'];

export interface InsuredPersonInterface {
  firstName: string;
  lastName: string;
  birthDate: string;
  child: boolean;
}

export interface InsuredPersonsFormValues {
  insuredPersons: InsuredPersonInterface[];
}

interface InsuredPersonsFormProps {
  insuredPersonsData?: InsuredPersonsFormValues;
  formItems?: Array<InsuredPersonFormItemType>;
  numOfInsuredAdults: number;
  numOfInsuredChildren: number;
  onSubmit?: (values: InsuredPersonsFormValues) => void;
}

export const InsuredPersonsFormItem = styled.div({
  marginBottom: '24px'
});

export const InsuredPersonsForm = forwardRef<FormRef, InsuredPersonsFormProps>(({
  insuredPersonsData,
  formItems = allInsuredPersonDataFormItems,
  numOfInsuredAdults,
  numOfInsuredChildren,
  onSubmit
}, ref) => {
  // Return null if no insured persons
  if (numOfInsuredAdults === 0 && numOfInsuredChildren === 0) {
    return null;
  }

  const [formValues, setFormValues] = useState<InsuredPersonsFormValues>({
    insuredPersons: Array.from({ length: numOfInsuredAdults + numOfInsuredChildren }, (_, index) => {
      const existingPerson = insuredPersonsData?.insuredPersons?.[index];
      return {
        firstName: existingPerson?.firstName || '',
        lastName: existingPerson?.lastName || '',
        birthDate: existingPerson?.birthDate || '',
        child: index >= numOfInsuredAdults,
      };
    })
  });


  useEffect(() => {
    const totalInsured = numOfInsuredAdults + numOfInsuredChildren;
    setFormValues(prev => {
      const currentLength = prev.insuredPersons.length;
      
      // If the total number hasn't changed, don't recreate the array
      if (currentLength === totalInsured) {
        return prev;
      }
      
      // If the total number has changed, recreate the array but preserve existing data
      return {
        ...prev,
        insuredPersons: Array.from({ length: totalInsured }, (_, index) => {
          // First try to get existing data from current form values
          const existingPerson = prev.insuredPersons[index];
          if (existingPerson) {
            return {
              ...existingPerson,
              child: index >= numOfInsuredAdults, // Update child status
            };
          }
          
          // If no existing data, try to get from initial data
          const initialPerson = insuredPersonsData?.insuredPersons?.[index];
          return {
            firstName: initialPerson?.firstName || '',
            lastName: initialPerson?.lastName || '',
            birthDate: initialPerson?.birthDate || '',
            child: index >= numOfInsuredAdults,
          };
        })
      };
    });
  }, [numOfInsuredAdults, numOfInsuredChildren, insuredPersonsData]);


  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleFieldChange = (personIndex: number, fieldName: keyof InsuredPersonInterface, value: string) => {
    setFormValues(prev => ({
      ...prev,
      insuredPersons: prev.insuredPersons.map((person, index) =>
        index === personIndex
          ? { ...person, [fieldName]: value }
          : person
      )
    }));

    // Clear error when user starts typing
    const errorKey = `insuredPersons.${personIndex}.${fieldName}`;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  const checkIfYoungerThan18 = (birthDate: string): boolean => {
    const today = dayjs();
    const birth = dayjs(birthDate);
    const age = today.diff(birth, 'year');
    return age < 18;
  };


  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    formValues.insuredPersons.forEach((person, personIndex) => {
      if (formItems.includes('firstName') && !person.firstName.trim()) {
        newErrors[`insuredPersons.${personIndex}.firstName`] = i18n.t('components.forms.insuredPersons.firstNameRequired');
      }

      if (formItems.includes('lastName') && !person.lastName.trim()) {
        newErrors[`insuredPersons.${personIndex}.lastName`] = i18n.t('components.forms.insuredPersons.lastNameRequired');
      }

      if (formItems.includes('birthDate') && !person.birthDate) {
        newErrors[`insuredPersons.${personIndex}.birthDate`] = i18n.t('components.forms.insuredPersons.birthDateRequired');
      }

      if (formItems.includes('birthDate') && person.child && !checkIfYoungerThan18(person.birthDate)) {
        newErrors[`insuredPersons.${personIndex}.birthDate`] = i18n.t('components.forms.insuredPersons.childMustBeYoungerThan18');
      }

      if (formItems.includes('birthDate') && !person.child && !checkIfOlderThan18(person.birthDate)) {
        newErrors[`insuredPersons.${personIndex}.birthDate`] = i18n.t('components.forms.insuredPersons.adultMustBeOlderThan18');
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm() && onSubmit) {
      onSubmit(formValues);
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: handleSubmit,
    getValues: () => formValues,
    setFieldValue: (fieldName: string, value: any) => {
      const parts = fieldName.split('.');
      if (parts.length === 3 && parts[0] === 'insuredPersons') {
        const personIndex = parseInt(parts[1]);
        const field = parts[2] as keyof InsuredPersonInterface;
        if (!isNaN(personIndex) && personIndex >= 0 && personIndex < numOfInsuredAdults + numOfInsuredChildren) {
          handleFieldChange(personIndex, field, value);
        }
      }
    },
    validateForm,
    scrollIntoView: () => { }
  }));

  return (
    <>
      {formValues.insuredPersons.map((person, personIndex) => (
        <CardWithTitle title={person.child ? `${i18n.t('components.forms.insuredPersons.child', { index: personIndex + 1})}` : `${i18n.t('components.forms.insuredPersons.insuredPerson')} ${personIndex + 1}`} key={personIndex}>
          {formItems.includes('firstName') && (
            <InsuredPersonsFormItem>
              <InputText
                label={`${i18n.t('components.forms.insuredPersons.firstName')}`}
                value={person.firstName}
                onChange={(value) => handleFieldChange(personIndex, 'firstName', value)}
                error={errors[`insuredPersons.${personIndex}.firstName`]}
              />
            </InsuredPersonsFormItem>
          )}

          {formItems.includes('lastName') && (
            <InsuredPersonsFormItem>
              <InputText
                label={`${i18n.t('components.forms.insuredPersons.lastName')}`}
                value={person.lastName}
                onChange={(value) => handleFieldChange(personIndex, 'lastName', value)}
                error={errors[`insuredPersons.${personIndex}.lastName`]}
              />
            </InsuredPersonsFormItem>
          )}

          {formItems.includes('birthDate') && (
              <InputBirthDate
                label={`${i18n.t('components.forms.insuredPersons.birthDate')}`}
                value={person.birthDate}
                onChange={(value) => handleFieldChange(personIndex, 'birthDate', value)}
                error={errors[`insuredPersons.${personIndex}.birthDate`]}
              />
          )}
        </CardWithTitle>
      ))}
    </>
  );
});
